import React from 'react';
import { connect, useSelector } from 'react-redux';
import { Card, Form, Row, Col } from 'react-bootstrap-5';
import classNames from 'classnames';

import ProductFeature from '../product_feature';
import { setOffMarketHoverPropertyId } from 'bundles/connect/components/store/actions/ui';
import { setOffMarketPropertyId } from 'bundles/connect/components/store/actions/off_market_property';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRectangleHistoryCirclePlus } from '@fortawesome/pro-solid-svg-icons';

import OffMarketPropertyImage from './off_market_property_image';
import OffMarketSummary from './off_market_summary';
import PropertyBookmark from './property_bookmark';
import AddToList from '../../../properties/add_to_list';
import EmbeddedMode from '../../../embedded_mode';
import AddToRethinkList from 'bundles/connect/components/off_market_properties/add_to_rethink/add_to_rethink_list';
import AddPropertyToRethink from '../../../off_market_properties/add_to_rethink/add_property_to_rethink';
import { selectLoading } from 'bundles/connect/components/store/selectors/ui';
import OffMarketResultsLoader from './off_market_result_loader';
import type { InsightsProperty } from 'bundles/connect/types';

type OffMarketResultCardProps = {
  isSelected: boolean;
  offMarketHoverPropertyId?: string;
  result: InsightsProperty;
  setOffMarketHoverPropertyId: (id?: string) => void;
  setOffMarketPropertyId: (id: string) => void;
  onSelectToggle: (id: string) => void;
}

function OffMarketResultCard({
  isSelected,
  offMarketHoverPropertyId,
  result,
  setOffMarketPropertyId,
  setOffMarketHoverPropertyId,
  onSelectToggle
}: OffMarketResultCardProps) {
  const loading = useSelector(selectLoading);
  const isHovering = result.propertyId == offMarketHoverPropertyId;

  const onMouseEnter = () => {
    setOffMarketHoverPropertyId(result.propertyId);
  };

  const onMouseLeave = () => {
    setOffMarketHoverPropertyId();
  };

  const onClick = () => {
    onMouseLeave();
    setOffMarketPropertyId(result.propertyId);
  };

  const handleSelectToggle = () => onSelectToggle(result.propertyId);

  if (loading) {
    return (
      <OffMarketResultsLoader />
    );
  }

  return (
    <Card
      className={classNames('mb-3 overflow-hidden', {
        shadow: isHovering,
        'border-primary': isHovering,
      })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Row className="g-0 align-items-stretch">
        <Col className="d-flex flex-column" onClick={onClick}>
          <div className="d-flex flex-grow-1">
            <ProductFeature product="prospecting">
              <div className="py-2 ps-2" onClick={e => e.stopPropagation()}>
                <Form.Check
                  checked={isSelected}
                  data-pendo-tag="select_property_result_card"
                  id={`property_${result.propertyId}_checkbox`}
                  type="checkbox"
                  onChange={handleSelectToggle}
                />
              </div>
            </ProductFeature>
            <Card.Body className="p-2 clickable">
              <OffMarketSummary property={result} />
            </Card.Body>
          </div>
        </Col>
        <Col
          className="map-result-thumb off-market-result-thumb d-none d-md-block clickable"
          md={{ order: 'last', span: 'auto' }}
          xs={{ order: 'first', span: '12' }}
          onClick={onClick}
        >
          <OffMarketPropertyImage result={result} />
        </Col>
        <ProductFeature product="prospecting">
          <EmbeddedMode>
            <Col
              className="d-flex flex-column border-start"
              xs={{ order: 'last', span: 'auto' }}
            >
              <div className="flex-fill clickable border-bottom" data-pendo-tag="add_to_list_result_card">
                <AddToList itemCount={1} itemIds={new Set([result.propertyId])}>
                  <div className="align-content-center text-center p-2 h-100">
                    <FontAwesomeIcon
                      className="text-primary"
                      icon={faRectangleHistoryCirclePlus}
                    />
                  </div>
                </AddToList>
              </div>
              <div className="flex-fill clickable" data-pendo-tag="bookmark_property_result_card">
                <PropertyBookmark bookmarked={result.bookmarked || false} buttonClass="h-100" id={result.propertyId} />
              </div>
            </Col>
          </EmbeddedMode>
          <EmbeddedMode display>
            <Col
              className="d-flex flex-column border-start"
              xs={{ order: 'last', span: 'auto' }}
            >
              <div className="flex-fill clickable border-bottom" data-pendo-tag="add_to_rethink_list_result_card">
                <AddToRethinkList asIcon insightsPropertyId={result.propertyId} itemCount={1} />
              </div>
              <div className="flex-fill clickable" data-pendo-tag="bookmark_property_result_card">
                <AddPropertyToRethink asIcon insightsPropertyId={result.propertyId} lastFetched={null} />
              </div>
            </Col>
          </EmbeddedMode>
        </ProductFeature>
      </Row>
    </Card>
  );
}

const mapStateToProps = ({ ui: { offMarketHoverPropertyId } }) => ({
  offMarketHoverPropertyId
});
export default connect(mapStateToProps, {
  setOffMarketHoverPropertyId,
  setOffMarketPropertyId,
})(OffMarketResultCard);
